import React, { FC } from 'react'
import Head from 'next/head'
import SALink from 'components/SALink'
import styles from './Pagination.module.scss'
import classNames from 'classnames'
import PaginationMobileButton from './PaginationMobileButton'
import { getPageRangeNumbers } from './helpers'

type PageLinkProps = {
  pageNumber: number | null
  onClickChangePage: (page: number) => (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  getUrlWithParam: ({ page }: { page: number }) => string
  isSelected?: boolean
  isVisible?: boolean
}

const PageLink = ({
  pageNumber,
  onClickChangePage,
  getUrlWithParam,
  isSelected,
  isVisible,
}: PageLinkProps) => {
  return (
    <SALink
      key={pageNumber}
      tabIndex={0}
      role='button'
      href={
        pageNumber
          ? getUrlWithParam({
              page: pageNumber,
            })
          : undefined
      }
      onClick={pageNumber ? onClickChangePage(pageNumber) : undefined}
      data-value={pageNumber}
      className={classNames(styles.pageLink, {
        [styles.pageLinkSelected]: isSelected,
        [styles.pageLinkHidden]: !isVisible,
      })}
      aria-label={`Go to page ${pageNumber}`}
      title={`Page ${pageNumber}`}
    >
      {pageNumber}
    </SALink>
  )
}

type PaginationProps = {
  current: number
  total: number
  onClickLink: ({ page, currentHref }: { page: number; currentHref: string }) => void
  urlWithParam: ({ page }: { page: number }) => string
  isNextBackVisibleOnMobile?: boolean
}

const maxPagesToShow = 3

const Pagination: FC<PaginationProps> = ({
  current: currentPageNumber,
  total,
  onClickLink,
  urlWithParam: getUrlWithParam,
  isNextBackVisibleOnMobile = true,
}) => {
  if (!currentPageNumber || !total || total <= 1) {
    return null
  }

  const prevPage = currentPageNumber > 1 ? currentPageNumber - 1 : null
  const nextPage = currentPageNumber < total ? currentPageNumber + 1 : null
  let pagesStart = currentPageNumber - 1

  if (pagesStart < 1) {
    pagesStart = 1
  }

  if (pagesStart + (maxPagesToShow - 1) >= total) {
    pagesStart = total - maxPagesToShow + 1

    if (pagesStart < 1) {
      pagesStart = 1
    }
  }

  const handleClickChangePage = (page: number) => {
    return (ev: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      ev.preventDefault()
      const currentHref = ev?.currentTarget?.href
      onClickLink({ page, currentHref })
    }
  }

  const pageItems = getPageRangeNumbers(currentPageNumber, total)

  return (
    <div className={styles.paginationWrapper}>
      {/* SEO */}
      <Head>
        {prevPage && (
          <link rel='prev' href={getUrlWithParam({ page: prevPage === 1 ? 0 : prevPage })} />
        )}
        {nextPage && <link rel='next' href={getUrlWithParam({ page: nextPage })} />}
      </Head>

      <PaginationMobileButton
        direction='back'
        pageNumber={prevPage}
        onClickChangePage={handleClickChangePage}
        getUrlWithParam={getUrlWithParam}
        disabled={!prevPage}
        isIconVisible={currentPageNumber !== 1}
        isNextBackVisibleOnMobile={isNextBackVisibleOnMobile}
      />

      {pageItems.map((item, idx) =>
        item === 'ellipsis' ? (
          <div key={`ellipsis-${idx}`} className={styles.ellipses}>
            ...
          </div>
        ) : (
          <PageLink
            key={item}
            pageNumber={item}
            onClickChangePage={handleClickChangePage}
            getUrlWithParam={getUrlWithParam}
            isSelected={item === currentPageNumber}
            isVisible={true}
          />
        )
      )}

      <PaginationMobileButton
        direction='next'
        pageNumber={nextPage}
        onClickChangePage={handleClickChangePage}
        getUrlWithParam={getUrlWithParam}
        disabled={!nextPage}
        isIconVisible={currentPageNumber !== total}
        isNextBackVisibleOnMobile={isNextBackVisibleOnMobile}
      />
    </div>
  )
}

export default Pagination
